// Loads the archive search index once per session (1 document read) and keeps
// the prepared form in memory for every search after that.
import { useCallback, useEffect, useState } from "react";
import { prepare, type SearchIndexDoc } from "../../shared/searchIndex.ts";
import type { StoryRepository } from "./repository.ts";

type Prepared = ReturnType<typeof prepare>;
export interface LoadedIndex {
  doc: SearchIndexDoc;
  prepared: Prepared;
}

let cache: LoadedIndex | null = null;
let inflight: Promise<LoadedIndex | null> | null = null;

export function loadSearchIndex(repo: StoryRepository): Promise<LoadedIndex | null> {
  if (cache) return Promise.resolve(cache);
  inflight ??= repo
    .getSearchIndex()
    .then((doc) => (cache = doc ? { doc, prepared: prepare(doc.entries) } : null))
    .finally(() => (inflight = null));
  return inflight;
}

export function useSearchIndex(repo: StoryRepository | null, enabled: boolean) {
  const [state, setState] = useState<{ status: "idle" | "loading" | "ready" | "missing" | "error"; index: LoadedIndex | null; error?: string }>(
    cache ? { status: "ready", index: cache } : { status: "idle", index: null },
  );
  const load = useCallback(() => {
    if (!repo) return;
    setState((s) => (s.status === "ready" ? s : { status: "loading", index: null }));
    loadSearchIndex(repo)
      .then((index) => setState(index ? { status: "ready", index } : { status: "missing", index: null }))
      .catch((e) => setState({ status: "error", index: null, error: e instanceof Error ? e.message : "Search isn't available." }));
  }, [repo]);
  useEffect(() => {
    if (enabled && state.status === "idle") load();
  }, [enabled, state.status, load]);
  return { ...state, retry: load };
}
